import { AnyAction, combineReducers, Reducer } from "redux";
import produce from "immer";

import { createInstanceMapReducer, EMPTY_OBJECT } from "@multiweb/reactUtils";
import { IconCode } from "@multiweb/types/contracts";
import { LOC_KEY_NO_ITEMS, LOC_KEY_NO_ITEMS_FILTER, LOC_KEY_NO_ITEMS_FULLTEXT_SEARCH } from "./locKeys";
import {
    ActionArea,
    DefaultRowTemplate,
    EmptyMessageSettings,
    FramesMapState,
    IDeleteFrameAction,
    IFrameMappedAction,
    IFramesState,
    IInitFrameAction,
    IInitFrameWithDataSourceAction,
    ISetActionAreaAction,
    ISetAllowMultiselectAction,
    ISetCanValueLoadAction,
    ISetEmptyMessageCustomizationAction,
    ISetInteractiveAction,
    ISetLoadOnScrollAction,
    ISetMultiwebVisibilityAction,
    ISetRowTemplateAction,
    ISetShowHeaderAction,
    RowTemplateType,
} from "./types";

const canValueLoad: Reducer<boolean> = (state = true, action) => {
    if (action instanceof IInitFrameAction || action instanceof IInitFrameWithDataSourceAction) {
        return action.payload.initialCanValueLoad;
    } else if (action instanceof ISetCanValueLoadAction) {
        return action.payload.value;
    }
    return state;
};

const actionArea: Reducer<ActionArea> = (state = ActionArea.aaRight, action) => {
    if (action instanceof IInitFrameAction || action instanceof IInitFrameWithDataSourceAction) {
        return action.payload.frameConfig.initialActionArea ?? state;
    } else if (action instanceof ISetActionAreaAction) {
        return action.payload.actionArea;
    }
    return state;
};

const rowTemplate: Reducer<RowTemplateType> = (state = DefaultRowTemplate.singleLine, action) => {
    if (action instanceof IInitFrameAction || action instanceof IInitFrameWithDataSourceAction) {
        return action.payload.frameConfig.rowTemplate ?? state;
    } else if (action instanceof ISetRowTemplateAction) {
        return action.payload.rowTemplate;
    }
    return state;
};

const interactive: Reducer<boolean> = (state = true, action) => {
    if (action instanceof IInitFrameAction || action instanceof IInitFrameWithDataSourceAction) {
        return action.payload.initialInteractive;
    } else if (action instanceof ISetInteractiveAction) {
        return action.payload.value;
    }
    return state;
};

const loadOnScroll: Reducer<boolean> = (state = true, action) => {
    if (action instanceof IInitFrameAction || action instanceof IInitFrameWithDataSourceAction) {
        return action.payload.frameConfig.initialLoadOnScroll ?? state;
    } else if (action instanceof ISetLoadOnScrollAction) {
        return action.payload.loadOnScroll;
    }
    return state;
};

const showHeader: Reducer<boolean> = (state = false, action) => {
    if (action instanceof IInitFrameAction || action instanceof IInitFrameWithDataSourceAction) {
        return action.payload.frameConfig.initialShowHeader ?? state;
    } else if (action instanceof ISetShowHeaderAction) {
        return action.payload.showHeader;
    }
    return state;
};

const allowMultiselect: Reducer<boolean> = (state = true, action) => {
    if (action instanceof IInitFrameAction || action instanceof IInitFrameWithDataSourceAction) {
        return action.payload.frameConfig.allowMultiselect ?? state;
    } else if (action instanceof ISetAllowMultiselectAction) {
        return action.payload.allowMultiselect;
    }
    return state;
};

const visibility: Reducer<boolean> = (state = true, action) => {
    if (action instanceof ISetMultiwebVisibilityAction) {
        return action.payload.multiwebVisibility;
    }
    return state;
};

/**
 * Defaultní nastavení pro hlášku, která se zobrazí, pokud je seznam prázdný
 */
export const EMPTY_MESSAGE_CUSTOMIZATION_DEFAULT: EmptyMessageSettings = {
    noItemsLocKey: LOC_KEY_NO_ITEMS,
    noItemsIcon: IconCode.docArchiveEmpty,
    noItemsFilterLocKey: LOC_KEY_NO_ITEMS_FILTER,
    noItemsFilterIcon: IconCode.uiSearchCross,
    noItemsFulltextSearchLocKey: LOC_KEY_NO_ITEMS_FULLTEXT_SEARCH,
    noItemsFulltextSearchIcon: IconCode.uiSearchCross,
};

const noItemsLocKey: Reducer<string> = (state = EMPTY_MESSAGE_CUSTOMIZATION_DEFAULT.noItemsLocKey, action) => {
    if (action instanceof IInitFrameAction || action instanceof IInitFrameWithDataSourceAction) {
        return action.payload.frameConfig.emptyMessageCustomization?.noItemsLocKey ?? state;
    } else if (action instanceof ISetEmptyMessageCustomizationAction) {
        return action.payload.emptyMessageCustomization.noItemsLocKey ?? state;
    }
    return state;
};

const noItemsIcon: Reducer<IconCode> = (state = EMPTY_MESSAGE_CUSTOMIZATION_DEFAULT.noItemsIcon, action) => {
    if (action instanceof IInitFrameAction || action instanceof IInitFrameWithDataSourceAction) {
        return action.payload.frameConfig.emptyMessageCustomization?.noItemsIcon ?? state;
    } else if (action instanceof ISetEmptyMessageCustomizationAction) {
        return action.payload.emptyMessageCustomization.noItemsIcon ?? state;
    }
    return state;
};

const noItemsFilterLocKey: Reducer<string> = (state = EMPTY_MESSAGE_CUSTOMIZATION_DEFAULT.noItemsFilterLocKey, action) => {
    if (action instanceof IInitFrameAction || action instanceof IInitFrameWithDataSourceAction) {
        return action.payload.frameConfig.emptyMessageCustomization?.noItemsFilterLocKey ?? state;
    } else if (action instanceof ISetEmptyMessageCustomizationAction) {
        return action.payload.emptyMessageCustomization.noItemsFilterLocKey ?? state;
    }
    return state;
};

const noItemsFilterIcon: Reducer<IconCode> = (state = EMPTY_MESSAGE_CUSTOMIZATION_DEFAULT.noItemsFilterIcon, action) => {
    if (action instanceof IInitFrameAction || action instanceof IInitFrameWithDataSourceAction) {
        return action.payload.frameConfig.emptyMessageCustomization?.noItemsFilterIcon ?? state;
    } else if (action instanceof ISetEmptyMessageCustomizationAction) {
        return action.payload.emptyMessageCustomization.noItemsFilterIcon ?? state;
    }
    return state;
};

const noItemsFulltextSearchLocKey: Reducer<string> = (state = EMPTY_MESSAGE_CUSTOMIZATION_DEFAULT.noItemsFulltextSearchLocKey, action) => {
    if (action instanceof IInitFrameAction || action instanceof IInitFrameWithDataSourceAction) {
        return action.payload.frameConfig.emptyMessageCustomization?.noItemsFulltextSearchLocKey ?? state;
    } else if (action instanceof ISetEmptyMessageCustomizationAction) {
        return action.payload.emptyMessageCustomization.noItemsFulltextSearchLocKey ?? state;
    }
    return state;
};

const noItemsFulltextSearchIcon: Reducer<IconCode> = (state = EMPTY_MESSAGE_CUSTOMIZATION_DEFAULT.noItemsFulltextSearchIcon, action) => {
    if (action instanceof IInitFrameAction || action instanceof IInitFrameWithDataSourceAction) {
        return action.payload.frameConfig.emptyMessageCustomization?.noItemsFulltextSearchIcon ?? state;
    } else if (action instanceof ISetEmptyMessageCustomizationAction) {
        return action.payload.emptyMessageCustomization.noItemsFulltextSearchIcon ?? state;
    }
    return state;
};

const emptyMessageCustomization: Reducer<EmptyMessageSettings> = combineReducers({
    noItemsLocKey,
    noItemsIcon,
    noItemsFilterLocKey,
    noItemsFilterIcon,
    noItemsFulltextSearchLocKey,
    noItemsFulltextSearchIcon,
});

const reactFrameReducer: Reducer<IFramesState> = combineReducers({
    canValueLoad,
    rowTemplate,
    actionArea,
    showHeader,
    loadOnScroll,
    interactive,
    allowMultiselect,
    visibility,
    emptyMessageCustomization,
});

/**
 * Resolver instanceKey z akce.
 *
 * @param action Akce, ve které resolver najde instanceKey.
 */
const instanceKeyResolver = (action: AnyAction) => {
    return action instanceof IFrameMappedAction ? action.meta.frameKey : undefined;
};

const instanceMapReducer = createInstanceMapReducer(reactFrameReducer, instanceKeyResolver);

export const reducer: Reducer<FramesMapState> = (state = EMPTY_OBJECT, action) => {
    if (action instanceof IDeleteFrameAction) {
        return produce(state, (draft) => {
            delete draft[action.meta.frameKey];
        });
    } else {
        const frameKey = instanceKeyResolver(action);
        if (
            frameKey &&
            (state[frameKey] || action instanceof IInitFrameAction || action instanceof IInitFrameWithDataSourceAction)
        ) {
            return instanceMapReducer(state, action);
        }
        return state;
    }
};
